/**
 * PanelModal — the ONE centered-overlay primitive for every popup on the
 * Integrations page (2026-08-18 UX overhaul): setup fields, access controls,
 * per-app management, the desktop sign-in surface. Generalized from the
 * setup-fields dialog's overlay.
 *
 * Behavior: centered card over a warm scrim, ESC and backdrop-click close
 * (unless dismissable={false}), body scroll suppressed while open, focus
 * moved into the card on mount. Width is the caller's; height caps at 86vh
 * with internal scrolling.
 *
 * IT OPENS IN FRONT OF THE CHAT, AND A CLICK INSIDE IT DOES NOT CLOSE THE
 * CHAT (2026-09-11). Three properties, all load-bearing, all learned from
 * one report — a user in full-screen chat pressed the in-chat button to sign
 * into Gmail and the "which inbox?" prompt opened behind the pane that asked
 * for it, leaving the button on "connecting…" forever:
 *
 *  - The default rung is APP_OVERLAY_Z_INDEX, not a small number. It used to
 *    be a three-digit one, which lost to the full-screen chat — and to the
 *    docked and floating panes too, so it was never a full-screen-only bug.
 *    A caller that forgets `zIndex` now lands somewhere correct instead of
 *    invisible.
 *  - The root is PORTALED to document.body. The chat pane carries
 *    backdrop-filter and container-type, either of which makes it the
 *    containing block for position:fixed descendants — so a PanelModal ever
 *    rendered inside the chat tree would be trapped in it at ANY z-index.
 *    The portal is what makes the rung mean something.
 *  - The root wears data-prest0n-portal="true". The chat's outside-click
 *    handler closes the pane on a mousedown that is neither inside the chat
 *    nor inside a marked portal, so without this the click that types into a
 *    sign-in prompt dismisses the conversation behind it (the ReactionPicker
 *    lesson, 2026-09-09).
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import panelTheme from '../panelTheme';
import { APP_OVERLAY_Z_INDEX } from '../../../styles/layers';

export interface PanelModalProps {
  children: React.ReactNode;
  onClose: () => void;
  /** CSS width, e.g. 'min(560px, 94vw)'. Default suits small dialogs. */
  width?: string;
  /** false = no ESC / backdrop close (a critical in-flight step). */
  dismissable?: boolean;
  zIndex?: number;
  /** Test hook rendered on the card. */
  testId?: string;
  ariaLabel?: string;
  /**
   * Pinned action area (2026-08-23 owner report: the sign-in surface's
   * done/later buttons scrolled below the card's own fold on short viewports
   * — "crammed at the bottom of the page"). A footer never scrolls: the body
   * scrolls above it and the actions stay visible.
   */
  footer?: React.ReactNode;
  /** Height cap for the card. Default suits small dialogs. */
  maxHeight?: string;
  /**
   * FILL, don't scroll (2026-09-03 owner directive: the sign-in window
   * "should take more space on the screen so the user can easily see").
   *
   * With a footer, the body normally scrolls and its children keep their own
   * heights — which is why the live sign-in view had to be pinned to a `vh`
   * clamp, and why making that clamp bigger would have pushed the controls
   * below the fold again on a short viewport (the exact 2026-08-23 report).
   *
   * `bodyFill` makes the body a non-scrolling flex column instead, so a child
   * marked `flex: 1` takes ALL the space left over after the header and the
   * pinned footer — as big as the window allows, and never one pixel bigger.
   * The surface yields first, automatically, on every viewport.
   */
  bodyFill?: boolean;
  /**
   * A DEFINITE height for the card (2026-09-04). Default undefined — every
   * other dialog keeps today's shrink-to-fit behaviour exactly.
   *
   * WHY THIS EXISTS: a flex column carrying only a `maxHeight` is
   * shrink-to-fit, so `flex: 1` on the body below has NO free space to
   * distribute and a filling child collapses to its own floor. That is how
   * the sign-in surface came to render ~240px tall inside a card 1500px
   * wide — the owner's "wider but not larger". A `maxHeight` alone can never
   * fix it, however large you make it.
   *
   * Plain `vh` only, same rule as `maxHeight` above: no `dvh`, no `min()`
   * mixing the two (see the comment on the card's maxHeight).
   */
  height?: string;
  /** The card element, for a caller that needs to fullscreen or measure it. */
  cardRef?: React.MutableRefObject<HTMLDivElement | null>;
  /**
   * Corner radius. A fullscreen card must pass '0' — a rounded card in the
   * top layer shows the UA's black ::backdrop at each corner.
   */
  radius?: string;
  /**
   * Drop the overlay's padding so a 100%/100% card is a TRUE cover
   * (2026-09-04, Fable review of #1586). Without it the CSS fullscreen
   * fallback left a 16px scrim gutter on every side — and that gutter was
   * also the backdrop-close target, so a thumb landing there destroyed the
   * sign-in the cover was meant to enlarge.
   */
  flush?: boolean;
  /**
   * Reserve the footer's tallest shape from the first paint, so rows that
   * arrive later (async chips, a toggle, an error line) fill held space
   * instead of shrinking the body and re-laying-out whatever fills it.
   */
  footerMinHeight?: string;
}

export const PanelModal: React.FC<PanelModalProps> = ({
  children,
  onClose,
  width = 'min(460px, 94vw)',
  dismissable = true,
  zIndex = APP_OVERLAY_Z_INDEX,
  testId,
  ariaLabel,
  footer,
  maxHeight = '86vh',
  bodyFill = false,
  height,
  cardRef: externalCardRef,
  radius = panelTheme.radiusModal,
  flush = false,
  footerMinHeight,
}) => {
  const cardRef = useRef<HTMLDivElement | null>(null);
  // One stable callback ref: an inline arrow here re-runs (null → node) on
  // every render, which is wasted work and a footgun for any consumer that
  // reacts to the ref changing.
  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    cardRef.current = node;
    if (externalCardRef) externalCardRef.current = node;
  }, [externalCardRef]);
  // Stamped whenever we leave fullscreen — see the Escape guard below.
  const lastFullscreenExitRef = useRef(0);

  useEffect(() => {
    const isFullscreen = () => {
      try {
        return !!(
          document.fullscreenElement
          || (document as unknown as { webkitFullscreenElement?: Element | null })
            .webkitFullscreenElement
        );
      } catch {
        return false;
      }
    };
    const onFullscreenChange = () => {
      if (!isFullscreen()) lastFullscreenExitRef.current = Date.now();
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !dismissable) return;
      // ESCAPE IS ALSO THE BROWSER'S EXIT-FULLSCREEN GESTURE (2026-09-04).
      // Whether a keydown is dispatched for the Escape that leaves
      // fullscreen differs by engine — Chrome and Firefox swallow it, others
      // do not. Unguarded, a user pressing Escape to come out of fullscreen
      // instead destroys a half-finished sign-in, closing the journey and
      // the VM session with it. So: never close while fullscreen, and not
      // for a moment afterwards either.
      if (isFullscreen()) return;
      if (Date.now() - lastFullscreenExitRef.current < 400) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    // Keep the page from scrolling underneath the overlay.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the dialog so keyboard users land inside it.
    cardRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);
      document.body.style.overflow = prevOverflow;
    };
  }, [dismissable, onClose]);

  const overlay = (
    <div
      data-prest0n-portal="true"
      onMouseDown={(e) => {
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed', inset: 0, background: panelTheme.overlayScrim,
        zIndex, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: flush ? 0 : '1rem',
      }}
    >
      <div
        ref={mergedRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        data-testid={testId}
        style={{
          background: '#fff', border: `1px solid ${panelTheme.borderSoft}`,
          borderRadius: radius,
          width, maxWidth: '100%', boxShadow: panelTheme.shadowModal,
          // Plain vh (review 2026-08-23): a `min(86vh, 92dvh)` needs dvh
          // support or the WHOLE declaration is dropped — leaving the card
          // with no height cap at all, worse than what it replaced. The
          // surface clamp inside the sign-in modal is what actually keeps
          // the controls reachable on short viewports. (2026-09-03: that
          // clamp is gone — `bodyFill` below makes the surface yield to the
          // footer by construction, which is what let the window grow.)
          maxHeight, outline: 'none',
          // A definite height only when a caller asks for one — see the prop.
          ...(height ? { height } : {}),
          ...(footer
            ? { display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }
            : { padding: '1.4rem 1.5rem', overflowY: 'auto' }),
        }}
      >
        {footer ? (
          <>
            <div
              data-testid={testId ? `${testId}-body` : undefined}
              style={{
                padding: '1.4rem 1.5rem', flex: 1, minHeight: 0,
                ...(bodyFill
                  ? { display: 'flex', flexDirection: 'column', overflow: 'hidden' }
                  : { overflowY: 'auto' }),
              }}
            >
              {children}
            </div>
            <div
              data-testid={testId ? `${testId}-footer` : undefined}
              style={{
                padding: '0.85rem 1.5rem', borderTop: `1px solid ${panelTheme.borderSoft}`,
                flexShrink: 0, background: '#fff',
                ...(footerMinHeight ? { minHeight: footerMinHeight, boxSizing: 'border-box' as const } : {}),
              }}
            >
              {footer}
            </div>
          </>
        ) : children}
      </div>
    </div>
  );

  // SSR / test environments without a document render nothing rather than
  // throwing: every caller here is client-only, but the guard costs nothing.
  return typeof document === 'undefined' ? overlay : createPortal(overlay, document.body);
};

export default PanelModal;
